import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Button, 
  Card, 
  CardActions, 
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  InputLabel,
  OutlinedInput,
  Stack,
  FormHelperText,
  Grid,
  Box,
  Select,
  MenuItem,
  Chip,
  SelectChangeEvent
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { Business } from '../../../types/UserTypes';
import axios from '../../../../services/ApiService';
import LoadingSpinner from '../../../support/LoadingSpinner';
import toast from 'react-hot-toast';
import { BVTConstants } from '../../../../utils/BVTConstants';
import { CompanyCategory } from '../../../types/DataTypes';

// Constants for business types, sizes, and support types
const BUSINESS_TYPES = [
  { value: BVTConstants.BUSINESS_TYPE_SOLE_TRADER, label: BVTConstants.BUSINESS_TYPE_SOLE_TRADER },
  { value: BVTConstants.BUSINESS_TYPE_PARTNERSHIP, label: BVTConstants.BUSINESS_TYPE_PARTNERSHIP },
  { value: BVTConstants.BUSINESS_TYPE_COMPANY, label: BVTConstants.BUSINESS_TYPE_COMPANY },
  { value: BVTConstants.BUSINESS_TYPE_NFP, label: BVTConstants.BUSINESS_TYPE_NFP },
  { value: BVTConstants.BUSINESS_TYPE_INCORPORATED_ASSOCIATION, label: BVTConstants.BUSINESS_TYPE_INCORPORATED_ASSOCIATION },
  { value: BVTConstants.BUSINESS_TYPE_ABORIGINAL_CORPORATION, label: BVTConstants.BUSINESS_TYPE_ABORIGINAL_CORPORATION },
  { value: BVTConstants.BUSINESS_TYPE_COOPERATIVE, label: BVTConstants.BUSINESS_TYPE_COOPERATIVE },
];

const BUSINESS_SIZES = [
  { value: 'MICRO', label: 'Micro (1-4 employees)' },
  { value: 'SMALL', label: 'Small (5-19 employees)' },
  { value: 'MEDIUM', label: 'Medium (20-199 employees)' },
  { value: 'LARGE', label: 'Large (200+ employees)' }
];

const SUPPORT_TYPES = [
  { value: BVTConstants.BUSINESS_SUPPORT_AREA_FINANCE, label: BVTConstants.BUSINESS_SUPPORT_AREA_FINANCE },
  { value: BVTConstants.BUSINESS_SUPPORT_AREA_GOVERNANCE, label: BVTConstants.BUSINESS_SUPPORT_AREA_GOVERNANCE },
  { value: BVTConstants.BUSINESS_SUPPORT_AREA_OPERATIONAL, label: BVTConstants.BUSINESS_SUPPORT_AREA_OPERATIONAL },
  { value: BVTConstants.BUSINESS_SUPPORT_AREA_GROWTH, label: BVTConstants.BUSINESS_SUPPORT_AREA_GROWTH },
  { value: BVTConstants.BUSINESS_SUPPORT_AREA_RECOVERY, label: BVTConstants.BUSINESS_SUPPORT_AREA_RECOVERY },
  { value: BVTConstants.BUSINESS_SUPPORT_AREA_STARTUP, label: BVTConstants.BUSINESS_SUPPORT_AREA_STARTUP },
  { value: BVTConstants.BUSINESS_SUPPORT_AREA_ESG, label: BVTConstants.BUSINESS_SUPPORT_AREA_ESG },
  { value: BVTConstants.BUSINESS_SUPPORT_AREA_ORIC, label: BVTConstants.BUSINESS_SUPPORT_AREA_ORIC },
  { value: BVTConstants.BUSINESS_SUPPORT_AREA_OTHER, label: BVTConstants.BUSINESS_SUPPORT_AREA_OTHER }
];

const GROSS_INCOME_OPTIONS = [
  { value: BVTConstants.BUSINESS_INCOME_SMALL, label: BVTConstants.BUSINESS_INCOME_SMALL },
  { value: BVTConstants.BUSINESS_INCOME_MEDIUM, label: BVTConstants.BUSINESS_INCOME_MEDIUM },
  { value: BVTConstants.BUSINESS_INCOME_LARGE, label: BVTConstants.BUSINESS_INCOME_LARGE }
];

const GROSS_ASSETS_OPTIONS = [
  { value: BVTConstants.BUSINESS_ASSET_SMALL, label: BVTConstants.BUSINESS_ASSET_SMALL },
  { value: BVTConstants.BUSINESS_ASSET_MEDIUM, label: BVTConstants.BUSINESS_ASSET_MEDIUM },
  { value: BVTConstants.BUSINESS_ASSET_LARGE, label: BVTConstants.BUSINESS_ASSET_LARGE }
];

const EMPLOYEES_OPTIONS = [
  { value: BVTConstants.BUSINESS_EMPLOYEE_SMALL, label: BVTConstants.BUSINESS_EMPLOYEE_SMALL },
  { value: BVTConstants.BUSINESS_EMPLOYEE_MEDIUM, label: BVTConstants.BUSINESS_EMPLOYEE_MEDIUM },
  { value: BVTConstants.BUSINESS_EMPLOYEE_LARGE, label: BVTConstants.BUSINESS_EMPLOYEE_LARGE }
];

function BusinessEditCorporateProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const userId = (location.state as { userId?: number })?.userId;
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [sectors, setSectors] = useState<CompanyCategory[]>([]);
  const [selectedSector, setSelectedSector] = useState<number>(0);
  const [selectedBusinessType, setSelectedBusinessType] = useState<string>('');
  const [selectedBusinessSize, setSelectedBusinessSize] = useState<string>('');
  const [selectedSupportRequired, setSelectedSupportRequired] = useState<string[]>([]);
  const [selectedGrossIncome, setSelectedGrossIncome] = useState<string>('');
  const [selectedGrossAssets, setSelectedGrossAssets] = useState<string>('');
  const [selectedEmployees, setSelectedEmployees] = useState<string>('');
  const [businessData, setBusinessData] = useState<Business>({
    businessId: 0,
    userId: 0,
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    contact: '',
    businessABN: '',
    legalName: '',
    tradeName: '',
    address: '',
    webLink: '',
    logoId: 0,
    documentId: 0,
    status: BVTConstants.ACTIVE,
    agentCompanyId: 0,
    agentCompanyName: '',
    assignments: [],
    serviceId: 0,
    sectorId: 0,
    businessType: '',
    businessSize: '',
    acnIcn: '',
    grossIncome: '',
    grossAssets: '',
    employees: '',
    supportRequired: [],
    createdAt: '',
    updatedAt: '',
    lastLoginAt: '',
  });

  useEffect(() => {
    fetchBusinessData();
    fetchSectors();
  }, []);

  useEffect(() => {
    if (businessData) {
      setLoading(true);
      if (businessData.sectorId) {
        setSelectedSector(businessData.sectorId);
      }
      
      if (businessData.businessType) {
        setSelectedBusinessType(businessData.businessType);
      }
      
      if (businessData.businessSize) {
        setSelectedBusinessSize(businessData.businessSize);
      }
      
      if (businessData.supportRequired && businessData.supportRequired.length > 0) {
        setSelectedSupportRequired(businessData.supportRequired);
      }

      if (businessData.grossIncome) {
        setSelectedGrossIncome(businessData.grossIncome);
      }

      if (businessData.grossAssets) {
        setSelectedGrossAssets(businessData.grossAssets);
      }

      if (businessData.employees) {
        setSelectedEmployees(businessData.employees);
      }
      setLoading(false);
    }
  }, [businessData]);

  const fetchBusinessData = async () => {
    try {
      setLoading(true);
      let response;
      if (userId) {
        response = await axios.post("/getBusinessByUserId", userId);
      } else {
        response = await axios.get("/getBusiness");
      }
      setBusinessData(response.data);
    } catch (error) {
      console.error('Error fetching business data.');
      toast.error("Failed to fetch business data");
    } finally {
      setLoading(false);
    }
  };

  const fetchSectors = async () => {
    setSectors([]);
    setLoading(true);
    try {
      const response = await axios.get('/getCompanyCategories');
      if (Array.isArray(response.data)) {
        setSectors(response.data);
      }
    } catch (error) {
      console.error('Error fetching sectors.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setBusinessData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSectorChange = (event: SelectChangeEvent<number>) => {
    const { value } = event.target;
    setSelectedSector(value as number);
    setBusinessData(prev => ({
      ...prev,
      sectorId: value as number
    }));
  };

  const handleBusinessTypeChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    setSelectedBusinessType(value);
    setBusinessData(prev => ({
      ...prev,
      businessType: value
    }));
  };

  const handleBusinessSizeChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    setSelectedBusinessSize(value);
    setBusinessData(prev => ({
      ...prev,
      businessSize: value
    }));
  };

  const handleSupportRequiredChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    setSelectedSupportRequired(typeof value === 'string' ? [] : value);
  };

  const handleGrossIncomeChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    setSelectedGrossIncome(value);
    setBusinessData(prev => ({
      ...prev,
      grossIncome: value
    }));
  };

  const handleGrossAssetsChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    setSelectedGrossAssets(value);
    setBusinessData(prev => ({
      ...prev,
      grossAssets: value
    }));
  };

  const handleEmployeesChange = (event: SelectChangeEvent<string>) => {
    const { value } = event.target;
    setSelectedEmployees(value);
    setBusinessData(prev => ({
      ...prev,
      employees: value
    }));
  };

  const handleSubmit = async () => {
    setErrors({});
    let newErrors: { [key: string]: string } = {};

    if (selectedSector === 0) {
      newErrors.sector = 'Please select a sector';
    }
    if (!selectedBusinessType) {
      newErrors.businessType = 'Please select a business type';
    }
    if (selectedBusinessType !== BVTConstants.BUSINESS_TYPE_ABORIGINAL_CORPORATION && !selectedBusinessSize) {
      newErrors.businessSize = 'Please select a business size';
    }
    if (selectedSupportRequired.length === 0) {
      newErrors.supportRequired = 'Please select at least one support area';
    }

    // Validate ORIC-specific fields
    if (selectedBusinessType === BVTConstants.BUSINESS_TYPE_ABORIGINAL_CORPORATION) {
      if (!businessData.acnIcn?.trim()) {
        newErrors.acnIcn = 'ACN/ICN is required for ORIC businesses';
      }
      if (!selectedGrossIncome) {
        newErrors.grossIncome = 'Consolidated Gross Income is required for ORIC businesses';
      }
      if (!selectedGrossAssets) {
        newErrors.grossAssets = 'Consolidated Gross Assets is required for ORIC businesses';
      }
      if (!selectedEmployees) {
        newErrors.employees = 'Number of Employees is required for ORIC businesses';
      }
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setLoading(true);
    try {
      const updatedData = {
        ...businessData,
        sectorId: selectedSector,
        businessType: selectedBusinessType,
        businessSize: selectedBusinessSize,
        supportRequired: selectedSupportRequired,
        grossIncome: selectedGrossIncome,
        grossAssets: selectedGrossAssets,
        employees: selectedEmployees
      };
      await axios.post("/updateBusinessCorporateProfile", updatedData);
      toast.success("Business corporate profile updated successfully");
      navigate(-1);
    } catch (error) {
      console.error('Error updating business corporate profile');
      toast.error("Failed to update business corporate profile");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet><title>BVT Business - Edit Corporate Profile</title></Helmet>
      <Stack spacing={3}>
        <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
          <Card>
            <CardHeader title="Edit Corporate Profile" />
            <Divider />
            <CardContent sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item md={12} xs={12}>
                  <FormControl fullWidth error={!!errors.sector}>
                    <InputLabel>Sector</InputLabel>
                    <Select
                      value={selectedSector}
                      onChange={handleSectorChange}
                      input={<OutlinedInput label="Sector" />}
                    >
                      {sectors.map((sector) => (
                        <MenuItem key={sector.catId} value={sector.catId}>
                          {sector.catName}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.sector && <FormHelperText>{errors.sector}</FormHelperText>}
                  </FormControl>
                </Grid>

                <Grid item md={12} xs={12}>
                  <FormControl fullWidth error={!!errors.businessType}>
                    <InputLabel>Business Type</InputLabel>
                    <Select
                      value={selectedBusinessType}
                      onChange={handleBusinessTypeChange}
                      input={<OutlinedInput label="Business Type" />}
                    >
                      {BUSINESS_TYPES.map((type) => (
                        <MenuItem key={type.value} value={type.value}>
                          {type.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.businessType && <FormHelperText>{errors.businessType}</FormHelperText>}
                  </FormControl>
                </Grid>

                {selectedBusinessType !== BVTConstants.BUSINESS_TYPE_ABORIGINAL_CORPORATION && (
                  <Grid item md={12} xs={12}>
                    <FormControl fullWidth error={!!errors.businessSize}>
                      <InputLabel>Business Size</InputLabel>
                      <Select
                        value={selectedBusinessSize}
                        onChange={handleBusinessSizeChange}
                        input={<OutlinedInput label="Business Size" />}
                      >
                        {BUSINESS_SIZES.map((size) => (
                          <MenuItem key={size.value} value={size.value}>
                            {size.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.businessSize && <FormHelperText>{errors.businessSize}</FormHelperText>}
                    </FormControl>
                  </Grid>
                )}

                {selectedBusinessType === BVTConstants.BUSINESS_TYPE_ABORIGINAL_CORPORATION && (
                  <>
                    <Grid item md={12} xs={12}>
                      <FormControl fullWidth error={!!errors.acnIcn}>
                        <InputLabel>ACN/ICN</InputLabel>
                        <OutlinedInput
                          name="acnIcn"
                          value={businessData.acnIcn || ''}
                          onChange={handleChange}
                          label="ACN/ICN"
                          inputProps={{ maxLength: 50 }}
                        />
                        {errors.acnIcn && <FormHelperText>{errors.acnIcn}</FormHelperText>}
                      </FormControl>
                    </Grid>

                    <Grid item md={12} xs={12}>
                      <FormControl fullWidth error={!!errors.grossIncome}>
                        <InputLabel>Consolidated Gross Income</InputLabel>
                        <Select
                          value={selectedGrossIncome}
                          onChange={handleGrossIncomeChange}
                          input={<OutlinedInput label="Consolidated Gross Income" />}
                        >
                          {GROSS_INCOME_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.grossIncome && <FormHelperText>{errors.grossIncome}</FormHelperText>}
                      </FormControl>
                    </Grid>

                    <Grid item md={12} xs={12}>
                      <FormControl fullWidth error={!!errors.grossAssets}>
                        <InputLabel>Consolidated Gross Assets</InputLabel>
                        <Select
                          value={selectedGrossAssets}
                          onChange={handleGrossAssetsChange}
                          input={<OutlinedInput label="Consolidated Gross Assets" />}
                        >
                          {GROSS_ASSETS_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.grossAssets && <FormHelperText>{errors.grossAssets}</FormHelperText>}
                      </FormControl>
                    </Grid>

                    <Grid item md={12} xs={12}>
                      <FormControl fullWidth error={!!errors.employees}>
                        <InputLabel>Number of Employees</InputLabel>
                        <Select
                          value={selectedEmployees}
                          onChange={handleEmployeesChange}
                          input={<OutlinedInput label="Number of Employees" />}
                        >
                          {EMPLOYEES_OPTIONS.map((option) => (
                            <MenuItem key={option.value} value={option.value}>
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.employees && <FormHelperText>{errors.employees}</FormHelperText>}
                      </FormControl>
                    </Grid>
                  </>
                )}

                <Grid item md={12} xs={12}>
                  <FormControl fullWidth error={!!errors.supportRequired}>
                    <InputLabel>Area of Support Required</InputLabel>
                    <Select
                      multiple
                      value={selectedSupportRequired}
                      onChange={handleSupportRequiredChange}
                      input={<OutlinedInput label="Area of Support Required" />}
                      renderValue={(selected) => (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {selected.map((value) => {
                            const supportType = SUPPORT_TYPES.find(type => type.value === value);
                            return (
                              <Chip 
                                key={value} 
                                label={supportType ? supportType.label : value} 
                              />
                            );
                          })}
                        </Box>
                      )}
                    >
                      {SUPPORT_TYPES.map((support) => (
                        <MenuItem key={support.value} value={support.value}>
                          {support.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.supportRequired && <FormHelperText>{errors.supportRequired}</FormHelperText>}
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
            <Divider />
            <CardActions sx={{ justifyContent: 'flex-end' }}>
              <Button variant="contained" color='error' onClick={() => navigate(-1)}>Cancel</Button>
              <Button variant="contained" type="submit">Update</Button>
            </CardActions>
          </Card>
        </form>
        <LoadingSpinner loading={loading} />
      </Stack>
    </>
  );
}

export default BusinessEditCorporateProfile; 